import { call, select } from 'redux-saga/effects';
import {
  computableContractsDefaultProductIdSelector,
  diagnosisTreeContextSelector,
  selectProvider,
  serviceQualifierTreeSelector,
  servicesTreeContextSelector,
} from 'redux/selectors/config';
import {
  iCVCOptionsSelector,
  iCVCProductIdSelector,
  iCVCEffectivePeriodSelector,
} from 'redux/selectors/icvc';
import { providerToCountry } from 'mappers/providers';
import {
  currentDTAnswersSelector,
  currentDTSelector,
  currentDTServiceSelector,
  diagnosisPathSelector,
  interventionObjectsContextSelector,
} from 'redux/selectors/localDecisionTree';
import { DecisionTreeType } from 'ts/types/decision-tree.types';
import { currentDTQuestionSelector } from 'redux/selectors/decisionTreeCurrentStep';
import { incidentDateSelector } from 'redux/selectors/caseCreation';

export function* getAllQuestions({ answer, question }) {
  const selectedDT = yield select(currentDTSelector);
  let contextQuestions;
  const previousTreeAnsweredQuestions = [];
  if (selectedDT === DecisionTreeType.DIAGNOSIS) {
    contextQuestions = yield getDiagnosisDTContext();
  } else {
    const diagnosisTreeAnsweredQuestions = yield select(diagnosisPathSelector);
    previousTreeAnsweredQuestions.push(...diagnosisTreeAnsweredQuestions);
    contextQuestions = yield getServiceDTContext();
  }
  const currentDTAnswers = yield select(currentDTAnswersSelector);
  const currentQuestion = yield select(currentDTQuestionSelector);
  const allQuestions = [
    ...contextQuestions,
    ...previousTreeAnsweredQuestions,
    ...currentDTAnswers,
  ].filter(({ id }) => id !== currentQuestion.id);
  if (answer !== undefined && question !== 'SERVICES') {
    const lastAnswer = {
      id: currentQuestion.id,
      value: answer,
      text: question,
    };
    allQuestions.push(lastAnswer);
  }
  const validStateQuestions = [];
  allQuestions.forEach(({ id, value, text }) => {
    if (value?.includes('|')) {
      const values = value.split('|');
      validStateQuestions.push(
        ...values.map(val => ({ id, value: val, text })),
      );
    } else {
      validStateQuestions.push({ id, value, text });
    }
  });
  return removeDuplicates(validStateQuestions);
}

export function* getProductId(desiredDT) {
  const configEnv = process.env.REACT_APP_environment;
  const selectedDT = yield select(currentDTSelector);
  const serviceQualifierTreeConfig = yield select(serviceQualifierTreeSelector);
  const serviceQualifierTree = serviceQualifierTreeConfig[configEnv];
  if (
    selectedDT === DecisionTreeType.DIAGNOSIS ||
    desiredDT === DecisionTreeType.DIAGNOSIS
  ) {
    const icvcProductId = yield select(iCVCProductIdSelector);
    const defaultProductId = yield select(
      computableContractsDefaultProductIdSelector,
    );
    return icvcProductId || defaultProductId;
  } else {
    return serviceQualifierTree;
  }
}

export function* getIcvcDiagnosisDTContext() {
  const icvcEffectivePeriod = yield select(iCVCEffectivePeriodSelector);
  const icvcOptions = yield select(iCVCOptionsSelector);
  const icvcEffectivePeriodContext = [];
  if (icvcEffectivePeriod) {
    icvcEffectivePeriodContext.push({
      id: 'POLICY_START_DATE',
      value: new Date(icvcEffectivePeriod.start_date).toISOString(),
    });
  }
  const icvcOptionsContext = icvcOptions?.length
    ? icvcOptions.map(({ option_reference, option_value }) => ({
        id: option_reference,
        value: option_value,
      }))
    : [];
  return [...icvcEffectivePeriodContext, ...icvcOptionsContext];
}

export function* getDiagnosisDTContext() {
  const provider = yield select(selectProvider);
  const occurrenceDateAlreadyAnswered = yield select(incidentDateSelector);
  const diagnosisTreeContextQuestions = yield select(
    diagnosisTreeContextSelector,
  );
  const icvcDTContext = yield call(getIcvcDiagnosisDTContext);
  const occurrenceDate = [];
  if (occurrenceDateAlreadyAnswered) {
    occurrenceDate.push({
      id: 'OCCURRENCE_DATE',
      value: new Date(occurrenceDateAlreadyAnswered).toISOString(),
    });
  }
  return [
    ...diagnosisTreeContextQuestions,
    { id: 'COUNTRIES', value: providerToCountry[provider] },
    ...occurrenceDate,
    ...icvcDTContext,
  ];
}

export function* getServiceDTContext() {
  const currentService = yield select(currentDTServiceSelector);
  const provider = yield select(selectProvider);
  const icvcOptions = (yield select(iCVCOptionsSelector)) || [];
  const servicesTreeContextQuestions =
    (yield select(servicesTreeContextSelector)) || [];
  const interventionObjectsContext = yield select(
    interventionObjectsContextSelector,
  );
  return [
    ...servicesTreeContextQuestions,
    ...icvcOptions.map(({ option_reference, option_value }) => ({
      id: option_reference,
      value: option_value,
    })),
    ...interventionObjectsContext,
    { id: 'CONTRACT_COUNTRY', value: providerToCountry[provider] },
    { id: 'SERVICES', value: currentService },
  ];
}

//for common use, the object key order is important
const removeDuplicates = baseArray =>
  baseArray.filter(
    (firstObject, index) =>
      index ===
      baseArray.findIndex(
        otherObject =>
          JSON.stringify(firstObject) === JSON.stringify(otherObject),
      ),
  );
